import {
  addCurrentUserEventListener,
  getCurrentUser,
  removeCurrentUserEventListener,
  setUserClaims,
  UserPublicInfo
} from '../../webmodule-common/other/api/current-user';
import { appConfigMenuPageItems } from './app-config';
import { AppIndex, MenuItem } from '../../webmodule-common/other/app-index';
import { applyDebugInformation } from './debug';
import { attachRouter } from '../../webmodule-common/other/router';
import { customElement, query } from 'lit/decorators.js';
import { getAssetCdnPath } from '../../webmodule-common/other/common/assets';
import { getPageController } from '../../webmodule-common/other/ui/pages/page-base';
import { html, TemplateResult } from 'lit';
import { iconRegistry } from '../../webmodule-common/other/ui/icons/register-icons';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { injectThemeClasses } from '../../webmodule-common/other/domain';
import { noSqlDb, NoSqlDb } from '../../webmodule-common/database/no-sql-db';
import { rebindRouter } from './application-initialization';
import { registerIconLibrary } from '../../webmodule-common/components/src/webmodule-components';
import { internalComponentRegistry } from './componentRegistry';
import { tlang } from '../../webmodule-common/other/language/lang';
import { verifySession } from '../../webmodule-common/other/user-session-verifier';
import { webcomponentRegistry } from '../../webmodule-common/other/ui/webcomponent-registry';

// this is our concrete top level application, everything else should be generic
@customElement('wm-licenseserver-app-index')
export class WebModuleSupplierAppIndex extends AppIndex {
  protected noSqlDb: NoSqlDb = noSqlDb;

  // Do not remove below line as this will stop icons from working on our site.
  protected icons = iconRegistry;
  protected webcomponents = webcomponentRegistry;
  protected internalComponentRegistry = internalComponentRegistry;
  @query('#main-body')
  private mainBody!: HTMLElement;

  constructor() {
    super();
    applyDebugInformation();
    rebindRouter();
    console.log(this.noSqlDb.dbName());
    registerIconLibrary('system', {
      resolver: name => getAssetCdnPath(`/assets/icons/${name}.svg`)
    });

    registerIconLibrary('bootstrap', {
      resolver: name => `https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.4/icons/${name}.svg`
    });

    registerIconLibrary('fa', {
      resolver: name => {
        const filename = name.replace(/^fa[rbs]-/, '');
        let folder = 'regular';
        if (name.substring(0, 4) === 'fas-') folder = 'solid';
        if (name.substring(0, 4) === 'fab-') folder = 'brands';
        return `https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.5.2/svgs/${folder}/${filename}.svg`;
      },
      mutator: svg => svg.setAttribute('fill', 'currentColor')
    });

    this.userDisplayName = getCurrentUser()?.friendlyName;
  }

  async userStateChanged(_user: UserPublicInfo | null) {
    if (getCurrentUser() !== null) {
      await this.updateUIAspectsAfterLogin();
    } else this.userDisplayName = '';
  }

  generateBuildNumberText(): TemplateResult {
    const copyEvent = async () => {
      const text = `# Dealer's Module License Server
      
+ ${tlang`Location`} "${window.location.hostname}"     
+ ${tlang`Build "${this.buildNumber}`}"

            `;
      navigator?.clipboard?.writeText(text);
      await information(text, tlang`Copied to clipboard`);
    };

    function labelSpan(label: string, value: string) {
      return html`<span class="fw-bolder me-2">${label}</span><span class="me-2">${value}</span>`;
    }

    return html`${labelSpan(tlang`Build`, `${this.buildNumber ?? ''}`)}
      <span class="ms-2 me-2"><icon-copy @click=${copyEvent}></icon-copy></span>`;
  }

  connectedCallback(): void {
    super.connectedCallback();
    addCurrentUserEventListener(this);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    removeCurrentUserEventListener(this);
  }

  userNameMenuContent(): unknown {
    return html` <webmodule-icon library="fa" name="fas-user" slot="prefix"></webmodule-icon
      >${this.userDisplayName?.trim()}`;
  }

  customUserMenuElements(): TemplateResult {
    return html``;
  }

  async disconnectUser() {
    await getPageController().disposeAndRefresh();
    try {
      setUserClaims({});
    } catch {
      console.log(`Error disconnecting user`);
    }
    return true;
  }

  protected getMenuItems(): Array<MenuItem> {
    return appConfigMenuPageItems();
  }

  protected firstUpdated() {
    attachRouter(this.mainBody);
    let interval;
    const verifyUser = async () => {
      clearInterval(interval);
      await verifySession();
      interval = setInterval(verifyUser, 500);
    };
    interval = setInterval(verifyUser, 500);
    injectThemeClasses();
  }

  protected updateLogoTemplate() {
    if (this.outOfDate) return;
    const logoURl = '/assets/images/dls-logo.png';
    //TODO add logo as asset
    this.logoTemplate = logoURl
      ? html`<img src=${logoURl} crossorigin="anonymous" class="img-fluid" alt="Brand" width="90" />`
      : html` <svg width="1" height="1"></svg>`;
  }

  private async updateUIAspectsAfterLogin() {
    this.updateLogoTemplate();
    this.userDisplayName = getCurrentUser()?.friendlyName;
    this.requestUpdate();
  }
}
